import { jsonObjectSchema, jsonValueSchema } from "@arti/dashboard-events";
import {
  compileRecordingDispatchEnvelopeSchema,
  compileRecordingDispatchMetadataSchema,
} from "@arti/runtime-shared/compileRecording";
import {
  executionDispatchEnvelopeSchema,
  executionSetupDispatchEnvelopeSchema,
} from "@arti/runtime-shared/execution";
import {
  executionImplementMetadataSchema,
  executionSetupMetadataSchema,
} from "@arti/runtime-shared/execution/schemas/executionMetadata";
import {
  issueCommentIncompatibleTaskMetadataSchema,
  issueCommentIncompatibleTaskPayloadSchema,
} from "@arti/runtime-shared/issueCommentFlow";
import { planRevisionPlanPipelineMetadataSchema } from "@arti/runtime-shared/planRevision/metadata";
import { planRevisionPlanPipelineDispatchEnvelopeSchema } from "@arti/runtime-shared/planRevision/transport";
import {
  repoChatRetrieveDispatchEnvelopeSchema,
  repoIndexingDispatchEnvelopeSchema,
  repoIndexingTaskMetadataSchema,
} from "@arti/runtime-shared/sourceControl/transport";
import z from "zod";

// MARK: Reusable refinements

/** Matches safe identifier characters: a-zA-Z0-9._:- */
export const id64 = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-zA-Z0-9._:-]+$/);
const id128 = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[a-zA-Z0-9._:-]+$/);

/**
 * Boolean schema that also accepts the strings "true"/"false". Some LLM
 * callers quote boolean tool-call arguments; strict `z.boolean()` rejects
 * those. Avoids `z.coerce.boolean()`, which would treat the literal "false"
 * as truthy.
 */
export const looseBooleanSchema = z.preprocess((value) => {
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return value;
}, z.boolean());

// MARK: Param schemas

export const providerIdParamSchema = z.object({ providerId: id64 });

export const taskIdParamSchema = z.object({ taskId: id128 });

export const credentialIdParamSchema = z.object({ credentialId: id64 });

export const repositoryIdParamSchema = z.object({ repositoryId: id64 });

export const webhookIdParamSchema = z.object({ webhookId: id64 });

export const runIdParamSchema = z.object({ runId: id128 });

export const numericIdParamSchema = z.object({ id: z.coerce.number() });

export const snapshotIdParamSchema = z.object({ snapshotId: id128 });

export const revisionIdParamSchema = z.object({ revisionId: id64 });

export const flowIdParamSchema = z.object({ flowId: id128 });

export const storyIssueKeyParamSchema = z.object({ storyIssueKey: id128 });

export const issueKeyParamSchema = z.object({ issueKey: id128 });

export const organizationIdParamSchema = z.object({ organizationId: id128 });

export const memberIdParamSchema = z.object({ memberId: id128 });

export const userIdParamSchema = z.object({ userId: id128 });

export const inviteIdParamSchema = z.object({ inviteId: id128 });

export const entryIdParamSchema = z.object({ entryId: id128 });

export const inviteTokenParamSchema = z.object({
  token: z.string().trim().min(1),
});

// MARK: Compound param schemas

export const providerCredentialParamSchema = providerIdParamSchema.extend(
  credentialIdParamSchema.shape,
);

export const providerWebhookParamSchema = providerIdParamSchema.extend(
  webhookIdParamSchema.shape,
);

export const providerRepositoryParamSchema = providerIdParamSchema.extend(
  repositoryIdParamSchema.shape,
);

export const organizationMemberParamSchema = organizationIdParamSchema.extend(
  memberIdParamSchema.shape,
);

export const organizationInviteParamSchema = organizationIdParamSchema.extend(
  inviteIdParamSchema.shape,
);

// MARK: Common response schemas

export const errorResponseSchema = z.object({
  error: z.string(),
  message: z.string(),
});

// MARK: Task metadata/payload response schemas

export const taskCallbackResponseSchema = z
  .object({
    error: z.string().optional(),
    node_id: z.string().optional(),
    progress: z.number().optional(),
    result: jsonValueSchema.optional(),
    status: z.string().optional(),
    timestamp: z.number().optional(),
  })
  .passthrough()
  .meta({ ref: "TaskCallback" });

export const taskResultResponseSchema = z
  .object({
    actual_result: jsonValueSchema.optional(),
    expected_schema: jsonObjectSchema.optional(),
    expected_schema_name: z.string().optional(),
    validation_errors: z.array(z.string()).optional(),
  })
  .passthrough()
  .meta({ ref: "TaskResult" });

/**
 * Base task metadata shape for API responses.
 *
 * Typed with the fields every task metadata contains (from TaskMetadataBase).
 * `.passthrough()` allows extra per-task-type fields without breaking validation.
 */
export const genericTaskMetadataResponseSchema = z
  .object({
    flow: z
      .object({ kind: z.string().min(1) })
      .passthrough()
      .optional(),
    source_control_provider_id: z.string().nullable().optional(),
    source_control_repository_allowlist: z.array(z.string()).optional(),
    ticket_id: z.string().nullable().optional(),
  })
  .passthrough();

export const knownTaskMetadataResponseSchema = z.union([
  executionSetupMetadataSchema,
  executionImplementMetadataSchema,
  planRevisionPlanPipelineMetadataSchema,
  issueCommentIncompatibleTaskMetadataSchema,
  compileRecordingDispatchMetadataSchema,
  repoIndexingTaskMetadataSchema,
]);

/**
 * Task payload shape for API responses.
 *
 * Payloads vary significantly by task type. This provides the minimal
 * structural guarantee (it's an object) while allowing any fields.
 */
export const genericTaskPayloadResponseSchema = z.object({}).passthrough();

export const knownTaskPayloadResponseSchema = z.union([
  executionSetupDispatchEnvelopeSchema,
  executionDispatchEnvelopeSchema,
  planRevisionPlanPipelineDispatchEnvelopeSchema,
  issueCommentIncompatibleTaskPayloadSchema,
  compileRecordingDispatchEnvelopeSchema,
  repoIndexingDispatchEnvelopeSchema,
  repoChatRetrieveDispatchEnvelopeSchema,
]);

export const taskSnapshotResponseBaseSchema = z.object({
  assigned_node: z.string().nullable(),
  attempt_count: z.number(),
  completed_at: z.number().nullable(),
  created_at: z.number(),
  depends_on_task_id: z.string().nullable(),
  error: z.string().nullable(),
  last_callback: taskCallbackResponseSchema.nullable(),
  linked_task_id: z.string().nullable(),
  linked_ticket_id: z.string().nullable(),
  metadata: genericTaskMetadataResponseSchema,
  payload: genericTaskPayloadResponseSchema,
  result: taskResultResponseSchema.nullable(),
  started_at: z.number().nullable(),
  status: z.string(),
  task_id: z.string(),
  type: z.string(),
  updated_at: z.number(),
});

export const taskSnapshotResponseSchema = taskSnapshotResponseBaseSchema.meta({
  ref: "TaskSnapshot",
});

export type TaskSnapshotResponse = z.infer<typeof taskSnapshotResponseSchema>;
